import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import styled from 'styled-components';

import { FixtureState } from '@sorare/core/src/__generated__/globalTypes';
import { FixtureChart as DumbFixtureChart } from '@sorare/core/src/components/player/FixtureChart';

import { TeamLogo } from '@sorare/us-sports/src/components/team/TeamLogo';

import { FixtureChart_BaseballPlayer } from './__generated__/index.graphql';

type Props = {
  player: FixtureChart_BaseballPlayer;
  onClick?: (gameId: string) => void;
};

const SmallTeamLogo = styled(TeamLogo)`
  --size: 24px;
`;

export const FixtureChart = ({ player, onClick }: Props) => {
  // fixture are returned most recent first
  const fixturesStats = useMemo(
    () =>
      player.latestFixturesStats
        .filter(
          fixtureStat =>
            fixtureStat.fixture.fixtureState !== FixtureState.opened
        )
        .reverse(),
    [player.latestFixturesStats]
  );

  const playerTeamSlug = player.team?.slug;

  return (
    <DumbFixtureChart
      fixturesStats={fixturesStats.map(fixtureStats => {
        return {
          label: `GW${fixtureStats.fixture.gameWeek}`,
          games: [
            ...fixtureStats.status.gameStats.map(gs => {
              // a pitcher-batter (ohtani) can only get either a pitching score or a batting score in one game.
              const score = gs.score.pitching
                ? gs.score.pitching
                : gs.score.batting;

              return {
                id: gs.game.id as string,
                playerScore: gs.playedInGame ? score : undefined,
                startDate: gs.game.startDate,
                gameLabel: <SmallTeamLogo team={gs.againstTeam} />,
                ...(onClick ? { onClick: () => onClick?.(gs.game.id) } : {}),
              };
            }),
            ...fixtureStats.status.upcomingGames.map(ug => {
              return {
                id: ug.id as string,
                playerScore: undefined,
                upcoming: true,
                startDate: ug.startDate,
                gameLabel: (
                  <SmallTeamLogo
                    team={
                      ug.homeTeam.slug !== playerTeamSlug
                        ? ug.homeTeam
                        : ug.awayTeam
                    }
                  />
                ),
              };
            }),
          ],
        };
      })}
    />
  );
};

FixtureChart.fragments = {
  BaseballPlayer: gql`
    fragment FixtureChart_BaseballPlayer on BaseballPlayer {
      slug
      team {
        slug
      }
      latestFixturesStats: latestFixtureStats(last: 20) {
        fixture {
          id
          slug
          gameWeek
          startDate
          endDate
          fixtureState
        }
        status {
          gameStats {
            playedInGame
            score {
              pitching
              batting
            }
            game {
              id
              startDate
            }
            againstTeam {
              slug
              ...TeamLogo_SportsTeamInterface
            }
          }
          upcomingGames {
            id
            startDate
            homeTeam {
              slug
              ...TeamLogo_SportsTeamInterface
            }
            awayTeam {
              slug
              ...TeamLogo_SportsTeamInterface
            }
          }
        }
      }
    }
    ${TeamLogo.fragments.SportsTeamInterface}
  ` as TypedDocumentNode<FixtureChart_BaseballPlayer>,
};
